// FLAG:useDocuAi — pure classifier behind executeUpload's completion accounting. Kept as a leaf
// module (no store/FileService imports) so it is unit-testable in isolation.
//
// Classifies each queued upload against the live upload store as 'pending' | 'success' | 'failed'.
// Idempotent (safe to call on every store tick), so executeUpload can settle the moment every file
// reaches a terminal state — crucially including the failure paths that never mark a pending row
// `finished`:
//   • prepare-upload rejected (e.g. a 401/session drop or network error) leaves an errored
//     checksum/calculating entry and NO pending row is ever created;
//   • an upload chunk or the final complete-upload rejected leaves a pending row with an `error`.
// Before these were treated as terminal, executeUpload only settled via its 5-minute timeout, so
// the expense/revenue dropzone tile (which clears only once the promise settles) spun forever.

export type BatchUpload = {
  uuid: string,
  source?: string,
  name: string,
}

type CalculatingEntry = {
  uuid?: string,
  fileName?: string,
  error?: string | object,
}

type PendingEntry = {
  fileName?: string,
  error?: string | object,
  finished?: boolean,
  fileId?: string,
  uuid?: string,
}

export type UploadClassification = {
  uuid: string,
  source?: string,
  name: string,
  status: 'pending' | 'success' | 'failed',
  error?: string | object,
  fileId?: string,
}

export const classifyBatchUploads = ({
  uploads,
  pending = [],
  calculating = [],
}: {
  uploads: BatchUpload[],
  pending?: PendingEntry[],
  calculating?: CalculatingEntry[],
}): UploadClassification[] => {
  return uploads.map((upload) => {
    const base = { uuid: upload.uuid, source: upload.source, name: upload.name }

    // Prepare-upload failed before a pending row existed: the checksum/calculating entry (matched
    // precisely by the uuid executeUpload assigned) carries the error and is never removed.
    const calculation = calculating.find((item) => item.uuid === upload.uuid)
    if (calculation?.error) {
      return { ...base, status: 'failed' as const, error: calculation.error }
    }

    // Once prepare-upload succeeds the checksum entry is removed and a pending row appears.
    // Join by the batch uuid, which prepareUpload now stamps onto the row: joining by file name
    // (the previous convention) made two identically-named files in one drop — scanner output like
    // scan.pdf, or the same invoice dropped twice — share a single row, so one file's success or
    // failure was silently attributed to both.
    //
    // The name join is kept as a fallback for rows that genuinely carry no uuid: those restored by
    // fetchPendingUploads on page load, which belong to no batch in this session.
    const row = pending.find((item) => item.uuid && item.uuid === upload.uuid)
      ?? pending.find((item) => !item.uuid && item.fileName === upload.name)
    if (row?.error) {
      return { ...base, status: 'failed' as const, error: row.error }
    }
    if (row?.finished) {
      return { ...base, status: 'success' as const, fileId: row.fileId }
    }

    return { ...base, status: 'pending' as const }
  })
}
